import PropTypes from 'prop-types';
import { useState } from 'react';
import { ChevronRightIcon } from '@/ui/icons';
import './PanelPrimitives.css';

// ── Panel content primitives ─────────────────────────────────────────
// Display atoms for an entity's detail surface — label/value rows, titled
// groups, status badges, contextual icon CTAs. Composed identically across
// Causas, Diligencias, Receptores and Tribunales. Decoupled from the drawer
// shell (RightSidebar) so an inline-panel or full-page layout can use them
// without pulling in the drawer.

// A label/value row. Empty values collapse to a quiet em-dash; `mono` is for
// codes and figures (tabular numerals).
export function Field({ label, value, mono = false }) {
  const isEmpty = value == null || value === '' || value === '—';
  const cls = [
    'rsb-field__value',
    mono ? 'rsb-field__value--mono' : '',
    isEmpty ? 'rsb-field__value--empty' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className="rsb-field">
      <div className="rsb-field__label">{label}</div>
      <div className={cls}>{isEmpty ? '—' : value}</div>
    </div>
  );
}

Field.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node,
  mono: PropTypes.bool,
};

// Quiet icon action in a detail head (`.dd-iconbtn`, styles/detail.css —
// app-global, no import needed). Pass the icon SVG as children; `title` is the
// tooltip and, unless `ariaLabel` overrides it, the a11y name. Used by both
// by-id views (causa & diligencia).
export function DetailIconButton({
  title,
  ariaLabel,
  disabled,
  onClick,
  children,
}) {
  return (
    <button
      type="button"
      className="dd-iconbtn"
      title={title}
      aria-label={ariaLabel ?? title}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

DetailIconButton.propTypes = {
  title: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
};

// The detail aside's label/value rows (`.dd-info`, styles/detail.css): mono
// label | value grid, hairline-separated. `rows` is [label, value] pairs;
// empty values render "—", never hide. Used by both by-id views.
export function DetailInfoRows({ rows }) {
  return (
    <div className="dd-info">
      {rows.map(([k, v]) => (
        <div className="dd-info__row" key={k}>
          <div className="dd-k">{k}</div>
          <div className="dd-v">{v || '—'}</div>
        </div>
      ))}
    </div>
  );
}

DetailInfoRows.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.array).isRequired,
};

// Contextual CTA: a quiet icon button in the navy accent. Pass the icon SVG as
// children; `label` is the tooltip + a11y name. Used as a Section `action` or
// anywhere a contextual action is needed.
export function SidebarIconButton({ label, onClick, children }) {
  return (
    <button
      type="button"
      className="rsb-iconbtn"
      onClick={onClick}
      title={label}
      aria-label={label}
    >
      {children}
    </button>
  );
}

SidebarIconButton.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  children: PropTypes.node,
};

// Status badge — semantic dot + label. `kind` colors the dot only.
export function Badge({ label, kind = 'muted' }) {
  return (
    <span className={`rsb-badge rsb-badge--${kind}`}>
      <span className="rsb-badge__dot" aria-hidden="true" />
      {label}
    </span>
  );
}

Badge.propTypes = {
  label: PropTypes.node.isRequired,
  kind: PropTypes.oneOf(['ok', 'warn', 'info', 'err', 'muted']),
};

// Shared chevron (24-grid). The inline style keeps the rendered stroke at the
// weight `.rsb-section__chevron svg` was written for on the old 16-grid copy
// (1.5 on a 16 grid ≡ 2.25 on a 24 grid).
const CHEVRON = <ChevronRightIcon size={16} style={{ strokeWidth: 2.25 }} />;

// A titled group of fields, divided from the previous group by a hairline.
// `action` renders a right-aligned icon CTA in the header. `collapsible` makes
// the header a toggle that hides the fields behind a one-line `summary`
// (progressive disclosure for optional / high-volume detail).
export function Section({
  label,
  action,
  collapsible = false,
  defaultOpen = false,
  children,
}) {
  const [open, setOpen] = useState(defaultOpen);

  if (collapsible) {
    return (
      <section className="rsb-section">
        <button
          type="button"
          className={`rsb-section__toggle${open ? ' rsb-section__toggle--open' : ''}`}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {label != null && <div className="rsb-section__label">{label}</div>}
          <span
            className={`rsb-section__chevron${open ? ' rsb-section__chevron--open' : ''}`}
            aria-hidden="true"
          >
            {CHEVRON}
          </span>
        </button>
        {open && children}
      </section>
    );
  }

  if (action != null) {
    return (
      <section className="rsb-section">
        <div className="rsb-section__head">
          {label != null && <div className="rsb-section__label">{label}</div>}
          {action}
        </div>
        {children}
      </section>
    );
  }

  return (
    <section className="rsb-section">
      {label != null && <div className="rsb-section__label">{label}</div>}
      {children}
    </section>
  );
}

Section.propTypes = {
  label: PropTypes.node,
  action: PropTypes.node,
  collapsible: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  summary: PropTypes.node,
  children: PropTypes.node,
};
